/**
 * useUsedInventoryAgingData Hook
 *
 * Fetches the latest used-vehicle inventory-aging snapshot for the currently
 * selected location on the Forecast page (USED vehicle department). Backs the
 * "Inventory" collapsible card on the Used dept.
 */

import { useState, useEffect, useCallback } from 'react';
import { getUsedInventoryAging, UsedInventoryAgingResponse } from '../services/api';

interface UseUsedInventoryAgingDataParams {
  locationId: number;
  enabled: boolean;
}

interface UseUsedInventoryAgingDataResult {
  data: UsedInventoryAgingResponse | null;
  loading: boolean;
  error: string | null;
}

export function useUsedInventoryAgingData({
  locationId,
  enabled,
}: UseUsedInventoryAgingDataParams): UseUsedInventoryAgingDataResult {
  const [data, setData] = useState<UsedInventoryAgingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsedInventoryAging(locationId);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Failed to load used inventory aging data');
        setData(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setError(null);
      return;
    }
    fetchData();
  }, [locationId, enabled, fetchData]);

  return { data, loading, error };
}
